import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { Bell, Truck, FileWarning, FileText, Shield, AlertTriangle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const ICONS = { vehicle: Truck, fine: FileWarning, document: FileText, insurance: Shield, recall: AlertTriangle };

const dayLabel = (d) =>
  d < 0 ? `Overdue by ${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"}` : d === 0 ? "Due today" : `Due in ${d} day${d === 1 ? "" : "s"}`;

export default function NotificationsBell({ collapsed = false }) {
  const nav = useNavigate();
  const [data, setData] = useState({ count: 0, items: [] });
  const [open, setOpen] = useState(false);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);

  const go = (it) => {
    setOpen(false);
    if (it.category === "fine") nav("/fines");
    else if (it.category === "insurance") nav("/insurance");
    else if (it.vehicle_id) nav(`/vehicles/${it.vehicle_id}`);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-150 mb-3">
          <span className="relative">
            <Bell size={14} />
            {data.count > 0 && (
              <span data-testid="notifications-count" className="absolute -top-2 -right-2 bg-[#FF3B30] text-white text-[9px] font-bold min-w-[15px] h-[15px] px-0.5 flex items-center justify-center rounded-full">
                {data.count > 99 ? "99+" : data.count}
              </span>
            )}
          </span>
          <span className={collapsed ? "lg:hidden" : ""}>Notifications</span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="bg-card border-border rounded-none text-foreground w-96 p-0 max-h-[70vh] overflow-y-auto" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="font-heading font-black tracking-tighter text-lg">NOTIFICATIONS</span>
          <span className="text-xs text-muted-foreground">{data.count} due within 7 days</span>
        </div>
        {data.items.length === 0 && (
          <div className="px-4 py-10 text-center text-muted-foreground/70 text-sm" data-testid="notifications-empty">
            Nothing due in the next 7 days. All clear.
          </div>
        )}
        {data.items.map((it, i) => {
          const Icon = ICONS[it.category] || Truck;
          const overdue = it.days_left < 0 || it.category === "recall";
          return (
            <button
              key={i}
              data-testid={`notification-item-${i}`}
              onClick={() => go(it)}
              className="w-full text-left px-4 py-3 border-b border-border/60 hover:bg-muted/60 transition-colors duration-150 flex gap-3"
            >
              <Icon size={16} className={`mt-0.5 shrink-0 ${overdue ? "text-[#FF3B30]" : "text-amber-600 dark:text-[#FFD60A]"}`} />
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{it.registration} — {it.type}</div>
                <div className={`text-xs ${overdue ? "text-[#FF3B30]" : "text-amber-600 dark:text-[#FFD60A]"}`}>
                  {it.category === "recall" ? "DVSA outstanding recall — book repair with dealer" : `${dayLabel(it.days_left)} · ${fmtDate(it.due_date)}`}
                </div>
              </div>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
